import React, { useState } from 'react';
import { Modal, Button, Table, Form } from 'react-bootstrap';
import Swal from 'sweetalert2';

/**
 * ModalDuplicados - Modal to handle equipment duplicates
 * Shows table of duplicated equipment and allows selective addition
 * @param {Object} props - Component props
 * @param {boolean} props.show - Modal visibility state
 * @param {Function} props.onHide - Handler to close modal
 * @param {Object} props.data - Validation result with {nuevos: [], duplicados: []}
 * @param {Array} props.equiposYaAgregados - Array of equipos already added to the OT
 * @param {Function} props.onSelect - Handler receiving selected duplicate equipos
 * @returns {JSX.Element}
 */
const ModalDuplicados = ({ show, onHide, data = { nuevos: [], duplicados: [] }, equiposYaAgregados = [], onSelect }) => {
    const [selectedDuplicados, setSelectedDuplicados] = useState([]);

    const handleToggle = (equipo) => {
        setSelectedDuplicados(prev => {
            const exists = prev.find(e => e._id === equipo._id);
            if (exists) {
                return prev.filter(e => e._id !== equipo._id);
            }
            return [...prev, equipo];
        });
    };

    const handleToggleAll = (checked) => {
        if (checked) {
            // Only select equipos that are not already added
            const equiposDisponibles = data.duplicados.filter(eq => !isAlreadyAdded(eq._id));
            setSelectedDuplicados(equiposDisponibles);
        } else {
            setSelectedDuplicados([]);
        }
    };

    const isSelected = (equipoId) => {
        return selectedDuplicados.some(e => e._id === equipoId);
    };

    const isAlreadyAdded = (equipoId) => {
        return equiposYaAgregados.some(e => e._id === equipoId);
    };

    const equiposDisponibles = data.duplicados.filter(eq => !isAlreadyAdded(eq._id));
    const allSelected = equiposDisponibles.length > 0 && selectedDuplicados.length === equiposDisponibles.length;

    const handleConfirm = () => {
        // Filter out any equipos that are already added (extra safety)
        const equiposNuevos = selectedDuplicados.filter(eq => !isAlreadyAdded(eq._id));
        
        if (equiposNuevos.length === 0 && selectedDuplicados.length > 0) {
            Swal.fire({
                icon: 'warning',
                title: 'Equipos ya agregados',
                text: 'Todos los equipos seleccionados ya están en la OT'
            });
            return;
        }
        
        onSelect(equiposNuevos);
        setSelectedDuplicados([]);
    };

    const handleClose = () => {
        setSelectedDuplicados([]);
        onHide();
    };

    return (
        <Modal show={show} onHide={handleClose} size="lg">
            <Modal.Header closeButton>
                <Modal.Title>Equipos Duplicados Detectados</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <div className="mb-3">
                    <p className="mb-2">
                        <strong>Equipos nuevos:</strong> {data.nuevos.length} 
                        {data.nuevos.length > 0 && ' (se crearán automáticamente)'}
                    </p>
                    <p className="mb-2">
                        <strong>Equipos duplicados:</strong> {data.duplicados.length}
                        {data.duplicados.length > 0 && ' (seleccione los existentes que desea agregar a la OT)'}
                    </p>
                    {equiposDisponibles.length < data.duplicados.length && (
                        <p className="mb-2 text-warning">
                            <i className="bi bi-exclamation-triangle me-2"></i>
                            <strong>Nota:</strong> {data.duplicados.length - equiposDisponibles.length} equipo(s) ya están en la OT y no pueden agregarse nuevamente
                        </p>
                    )}
                </div>

                {data.duplicados.length > 0 && (
                    <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                        <Table striped bordered hover>
                            <thead style={{ position: 'sticky', top: 0, backgroundColor: 'white', zIndex: 1 }}>
                                <tr>
                                    <th>#</th>
                                    <th style={{ width: '60px', textAlign: 'center' }}>
                                        <input
                                            type="checkbox"
                                            checked={allSelected}
                                            onChange={(e) => handleToggleAll(e.target.checked)}
                                            style={{ transform: 'scale(1.3)' }}
                                        />
                                    </th>
                                    <th>Item</th>
                                    <th>Marca</th>
                                    <th>Modelo</th>
                                    <th>Serie</th>
                                    <th>Inventario</th>
                                    <th>Ubicación</th>
                                </tr>
                            </thead>
                            <tbody>
                                {data.duplicados.map((equipo, index) => {
                                    const yaAgregado = isAlreadyAdded(equipo._id);
                                    return (
                                        <tr 
                                            key={equipo._id || index}
                                            onClick={() => !yaAgregado && handleToggle(equipo)}
                                            style={{ 
                                                cursor: yaAgregado ? 'not-allowed' : 'pointer',
                                                opacity: yaAgregado ? 0.5 : 1
                                            }}
                                            className={isSelected(equipo._id) ? 'table-active' : ''}
                                        >
                                            <td>{index + 1}</td>
                                            <td style={{ textAlign: 'center', verticalAlign: 'middle' }} onClick={(e) => e.stopPropagation()}>
                                                <input
                                                    type="checkbox"
                                                    checked={isSelected(equipo._id)}
                                                    onChange={() => !yaAgregado && handleToggle(equipo)}
                                                    disabled={yaAgregado}
                                                    style={{ transform: 'scale(1.3)' }}
                                                />
                                            </td>
                                            <td>
                                                {equipo.item?.nombre || 'N/A'}
                                                {yaAgregado && <span className="badge bg-secondary ms-2">Ya en OT</span>}
                                            </td>
                                            <td>{equipo.marca || 'N/A'}</td>
                                            <td>{equipo.modelo || 'N/A'}</td>
                                            <td>{equipo.serie}</td>
                                            <td>{equipo.inventario || 'N/A'}</td>
                                            <td>{equipo.ubicacion || 'N/A'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </Table>
                    </div>
                )}

                {data.duplicados.length === 0 && (
                    <div className="text-center py-4 text-muted">
                        No se encontraron equipos duplicados
                    </div>
                )}
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>
                    Cancelar
                </Button>
                <Button 
                    variant="primary" 
                    onClick={handleConfirm}
                    disabled={data.nuevos.length === 0 && selectedDuplicados.length === 0}
                >
                    Continuar ({data.nuevos.length + selectedDuplicados.length} equipos)
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalDuplicados;
